import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillCancelTiming } from '../../../types';
import { DrillTile } from './DrillTile';
import '../../../styles/drill-noshow.css';

const tt = (key: string, opts?: Record<string, unknown>) =>
  i18n.t(`performance:noshowSubstance.timing.${key}`, opts);

function resaleBasis(timing: DrillCancelTiming): string {
  return tt(
    timing.resaleSource === 'config' ? 'resaleConfig' : 'resaleDefault',
    {
      hours: timing.resaleHours,
    },
  );
}

/**
 * When guests cancel: guest cancellations by the reservation's last change
 * against its start, and how many came early enough to resell the table.
 */
export function DrillCancelTimingSection({
  timing,
}: {
  timing?: DrillCancelTiming | null;
}) {
  const max = timing
    ? Math.max(...timing.buckets.map(b => b.sharePct), 0.0001)
    : 1;

  const rows = (withGuests: boolean) =>
    timing ? (
      <div className="ddrill-ns-rows">
        {timing.buckets.map(b => (
          <div
            className={`ddrill-ns-row${b.muted ? ' is-muted' : ''}`}
            key={b.key}
            data-muted={b.muted ? 'true' : undefined}
          >
            <span className="dcrux-si-name">{tt(`bucket.${b.key}`)}</span>
            <span className="dcrux-si-track">
              <span
                className="dcrux-si-fill"
                style={{ width: `${Math.max(2, (b.sharePct / max) * 100)}%` }}
              />
            </span>
            <span className="dcrux-si-val">
              {tt('bucketVal', { count: fmt.num(b.n), pct: b.sharePct })}
            </span>
            {withGuests ? (
              <span className="ddrill-ns-sub">
                {tt('guests', { count: fmt.num(b.guests) })}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    ) : null;

  const resellable = timing
    ? tt('resellable', {
        pct: timing.resellablePct,
        hours: timing.resaleHours,
        count: fmt.num(timing.resellable),
        n: fmt.num(timing.n),
      })
    : '';

  const inspector = useMemo(
    () =>
      timing ? (
        <>
          {rows(true)}
          <p className="ddrill-ns-note">{resellable}</p>
          <p className="ddrill-ns-note is-quiet">{resaleBasis(timing)}</p>
          {timing.medianHoursAhead != null ? (
            <p className="ddrill-ns-note">
              {tt('median', { hours: timing.medianHoursAhead })}
            </p>
          ) : null}
          <p className="ddrill-ns-note is-quiet">{tt('proxy')}</p>
          {timing.buckets.some(b => b.muted) ? (
            <p className="ddrill-ns-note is-quiet">
              {tt('muted', { min: timing.minSample })}
            </p>
          ) : null}
        </>
      ) : null,
    // rows / resellable derive from timing alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timing],
  );

  if (!timing) return null;

  return (
    <DrillTile
      sectionId="cancel-timing"
      span={4}
      title={tt('title')}
      meta={tt('meta', { n: fmt.num(timing.n) })}
      inspector={inspector}
    >
      {rows(false)}
      <p className="ddrill-ns-note">{resellable}</p>
      <p className="ddrill-ns-note is-quiet">{resaleBasis(timing)}</p>
    </DrillTile>
  );
}
